import React, { useEffect, useMemo, useState } from "react";
import PageHeader from "../components/PageHeader";
import { facultyList } from "../data/faculty";
import { programData } from "../data/program";
import { Users, Search, MapPin, X, Clock, CalendarDays, Star } from "lucide-react";

const TITLE_RE = /^(Dr\.?|Prof\.?|Dt\.?|Mr\.?|Ms\.?|Mrs\.?)\s+/i;
const normName = (s = "") =>
  s
    .replace(/^(prof\.?\s*dr|dr|prof|dt|mr|ms|mrs)\.?\s*/i, "")
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, "")
    .replace(/\s+/g, " ")
    .trim();
const initialsOf = (name = "") => {
  const parts = name.replace(TITLE_RE, "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return ((parts[0][0] || "") + (parts.length > 1 ? parts[parts.length - 1][0] || "" : "")).toUpperCase();
};
const monoTints = [
  "from-brand-500 to-brand-700",
  "from-navy-600 to-navy-800",
  "from-brand-400 to-brand-600",
  "from-navy-500 to-navy-700",
];
const tintFor = (name = "") => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % 997;
  return monoTints[h % monoTints.length];
};
const DAY_LABEL = ["Day 1 · 04 Sep", "Day 2 · 05 Sep", "Day 3 · 06 Sep"];
const PRES_RE = /paper presentation|oral paper|free paper|poster/i;

// ---- avatar (circle) -----------------------------------------------------
const CircleAvatar = ({ name, photo, className }) => {
  const [err, setErr] = useState(false);
  if (photo && !err)
    return (
      <img
        src={photo}
        alt={name}
        loading="lazy"
        onError={() => setErr(true)}
        className={`${className} rounded-full object-cover bg-slate-100`}
      />
    );
  return (
    <div
      className={`${className} rounded-full grid place-items-center font-display font-extrabold text-white bg-gradient-to-br ${tintFor(
        name
      )}`}
    >
      {initialsOf(name)}
    </div>
  );
};

// ---- small avatars for the modal -----------------------------------------
const SquareAvatar = ({ name, photo, active }) => {
  const [err, setErr] = useState(false);
  const ring = active ? "ring-2 ring-brand" : "ring-1 ring-slate-200";
  return (
    <div className={`flex-shrink-0 rounded-lg bg-white p-0.5 ${ring} shadow-sm`}>
      {photo && !err ? (
        <img
          src={photo}
          alt={name}
          loading="lazy"
          onError={() => setErr(true)}
          className="w-11 h-[52px] rounded-md object-cover bg-slate-100"
        />
      ) : (
        <div
          className={`w-11 h-[52px] rounded-md grid place-items-center text-white font-display font-bold text-xs bg-gradient-to-br ${tintFor(
            name
          )}`}
        >
          {initialsOf(name)}
        </div>
      )}
    </div>
  );
};
const RoundAvatar = ({ name, photo, active }) => {
  const [err, setErr] = useState(false);
  const ring = active ? "ring-2 ring-brand" : "ring-2 ring-white";
  if (photo && !err)
    return (
      <img
        src={photo}
        alt={name}
        loading="lazy"
        onError={() => setErr(true)}
        className={`w-11 h-11 rounded-full object-cover bg-slate-100 shadow-sm flex-shrink-0 ${ring}`}
      />
    );
  return (
    <div
      className={`w-11 h-11 rounded-full grid place-items-center text-white font-display font-bold text-xs shadow-sm flex-shrink-0 bg-gradient-to-br ${tintFor(
        name
      )} ${ring}`}
    >
      {initialsOf(name)}
    </div>
  );
};

// ---- name text (highlight the selected faculty) --------------------------
const NameText = ({ name, city, active }) => (
  <span className="leading-tight">
    <span className={active ? "font-bold text-brand-700" : "font-semibold text-navy-800"}>{name}</span>
    {city ? <span className="text-slate-400 font-normal"> · {city}</span> : null}
  </span>
);

// ---- one session rendered inside the modal -------------------------------
const RefSession = ({ refObj, facultyNorm }) => {
  const day = programData[refObj.d];
  const hall = day?.halls[refObj.h];
  const session = hall?.items[refObj.s];
  if (!session) return null;
  const isPres = PRES_RE.test(session.title || "");
  const roleLabel = refObj.roles.filter((r) => r !== "Speaker").join(" / ");
  const asSpeaker = refObj.roles.includes("Speaker");

  return (
    <div className="rounded-2xl border border-slate-200 overflow-hidden bg-white">
      {/* banner */}
      <div className="gradient-navy px-4 py-3 flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-white/12 px-3 py-1 text-xs font-semibold text-white ring-1 ring-white/15">
          <CalendarDays className="w-3.5 h-3.5 text-brand-300" />
          {DAY_LABEL[refObj.d]}
        </span>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-white/12 px-3 py-1 text-xs font-semibold text-white ring-1 ring-white/15">
          <MapPin className="w-3.5 h-3.5 text-brand-300" />
          {hall.hall}
        </span>
        {session.time && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/12 px-3 py-1 text-xs font-semibold text-white ring-1 ring-white/15">
            <Clock className="w-3.5 h-3.5 text-brand-300" />
            {session.time}
          </span>
        )}
      </div>
      <div className="p-4 md:p-5">
        {roleLabel && (
          <span className="inline-flex items-center gap-1 mb-2.5 rounded-full bg-brand/10 text-brand-700 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ring-1 ring-brand/20">
            <Star className="w-3 h-3" /> {roleLabel}
          </span>
        )}
        {session.title && (
          <h4 className="font-display font-bold text-navy-900 text-lg leading-snug mb-4">{session.title}</h4>
        )}

        {/* chairs / moderators / judges */}
        {session.people && session.people.length > 0 && (
          <div className="mb-4 flex flex-wrap gap-x-6 gap-y-3 rounded-xl bg-slate-50 border border-slate-100 p-3">
            {session.people.map((p, i) => {
              const mine = normName(p.name) === facultyNorm;
              return (
                <div key={i} className="flex items-center gap-2.5">
                  <SquareAvatar name={p.name} photo={p.photo} active={mine} />
                  <div className="leading-tight">
                    <div className="text-[10px] uppercase font-bold tracking-wide text-slate-400">{p.role}</div>
                    <NameText name={p.name} city={p.city} active={mine} />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* talks */}
        {session.talks && session.talks.filter((t) => !t.sublabel).length > 0 && (
          <div className="space-y-2.5">
            {session.talks
              .filter((t) => !t.sublabel)
              .map((t, i) => {
                const spk = t.speaker;
                const mine = spk && normName(spk) === facultyNorm;
                return (
                  <div
                    key={i}
                    className={`flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 rounded-xl border p-2.5 ${
                      mine ? "border-brand/30 bg-orange-50/50" : "border-slate-100 bg-white"
                    }`}
                  >
                    {t.time && (
                      <span className="self-start flex-shrink-0 inline-flex items-center justify-center rounded-lg bg-navy-800 text-white text-[11px] font-semibold px-2.5 py-1.5 whitespace-nowrap tabular-nums">
                        {t.time}
                      </span>
                    )}
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      {spk && !isPres && <RoundAvatar name={spk} photo={t.photo} active={mine} />}
                      <div className="min-w-0 flex-1">
                        {(t.title || "").trim() && (
                          <p className="text-sm text-navy-900 font-medium leading-snug">{t.title}</p>
                        )}
                        {spk && (
                          <p className="text-[13px] mt-0.5">
                            <NameText name={spk} city={t.city} active={mine} />
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>
    </div>
  );
};

// ---- modal ---------------------------------------------------------------
const FacultyModal = ({ faculty, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);
  if (!faculty) return null;
  const fnorm = normName(faculty.name);
  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center p-3 sm:p-6 bg-navy-900/60 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-4xl bg-slate-50 rounded-3xl shadow-2xl my-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* header */}
        <div className="sticky top-0 z-10 flex items-center gap-4 p-5 bg-white rounded-t-3xl border-b border-slate-100">
          <CircleAvatar name={faculty.name} photo={faculty.photo} className="w-16 h-16 text-lg ring-2 ring-brand/20" />
          <div className="min-w-0 flex-1">
            <h3 className="font-display font-extrabold text-navy-900 text-lg leading-tight">{faculty.name}</h3>
            {faculty.city && (
              <p className="text-sm text-brand-600 font-medium inline-flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5" /> {faculty.city}
              </p>
            )}
            <p className="text-xs text-slate-500 mt-0.5">
              {faculty.refs.length} session{faculty.refs.length !== 1 ? "s" : ""} at DRS 2026
            </p>
          </div>
          <button
            onClick={onClose}
            className="grid place-items-center w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 text-navy-700 transition-colors flex-shrink-0"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        {/* sessions */}
        <div className="p-5 space-y-4">
          {faculty.refs.length === 0 ? (
            <p className="text-center text-slate-500 py-8">No sessions listed.</p>
          ) : (
            faculty.refs.map((r, i) => <RefSession key={i} refObj={r} facultyNorm={fnorm} />)
          )}
        </div>
      </div>
    </div>
  );
};

// ---- card ----------------------------------------------------------------
const FacultyCard = ({ f, onClick }) => (
  <button onClick={onClick} className="group text-center focus:outline-none">
    <div className="relative mx-auto w-full aspect-square">
      <div className="card-hover w-full h-full rounded-full p-[3px] bg-gradient-to-br from-brand-300 via-brand to-navy-600 shadow-md">
        <CircleAvatar
          name={f.name}
          photo={f.photo}
          className="w-full h-full text-3xl ring-4 ring-white"
        />
      </div>
    </div>
    <h3 className="mt-3 font-display font-bold text-navy-900 text-sm leading-tight group-hover:text-brand transition-colors">
      {f.name}
    </h3>
    {f.city && (
      <p className="mt-0.5 inline-flex items-center gap-1 text-xs text-brand-600 font-medium">
        <MapPin className="w-3 h-3" />
        {f.city}
      </p>
    )}
  </button>
);

// ---- page ----------------------------------------------------------------
const Faculty = () => {
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return facultyList;
    return facultyList.filter(
      (f) => f.name.toLowerCase().includes(s) || (f.city || "").toLowerCase().includes(s)
    );
  }, [q]);

  // group A–Z by first letter of name (title stripped)
  const groups = useMemo(() => {
    const map = {};
    filtered.forEach((f) => {
      const letter = (normName(f.name)[0] || "#").toUpperCase();
      (map[letter] = map[letter] || []).push(f);
    });
    return Object.keys(map)
      .sort()
      .map((l) => ({ letter: l, items: map[l] }));
  }, [filtered]);

  return (
    <div>
      <PageHeader
        title="Faculty"
        crumb="Faculty"
        subtitle="Our distinguished national & international faculty for DRS 2026. Tap any faculty to see their sessions."
      />

      {/* meta + search */}
      <section className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
          <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-4 py-1.5 text-sm font-medium text-navy-800">
            <Users className="w-4 h-4 text-brand" /> {facultyList.length} Faculty
          </span>
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name or city…"
              className="w-full pl-10 pr-4 h-11 rounded-full border border-slate-200 bg-white text-sm text-navy-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand/40 transition"
            />
          </div>
        </div>
      </section>

      <section className="section-pad bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          {filtered.length === 0 ? (
            <p className="text-center text-slate-500 py-16">No faculty found for “{q}”.</p>
          ) : (
            groups.map((g) => (
              <div key={g.letter} className="mb-10">
                <div className="flex items-center gap-3 mb-6">
                  <span className="grid place-items-center w-9 h-9 rounded-xl bg-brand text-white font-display font-extrabold">
                    {g.letter}
                  </span>
                  <span className="h-px flex-1 bg-slate-200" />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-x-5 gap-y-8">
                  {g.items.map((f) => (
                    <FacultyCard key={f.name + f.city} f={f} onClick={() => setSelected(f)} />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {selected && <FacultyModal faculty={selected} onClose={() => setSelected(null)} />}
    </div>
  );
};

export default Faculty;
